// src/pages/equipamentos/Equipamentos.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTable, useFilters, useSortBy } from "react-table";
import { useLocation, useNavigate } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import { abrirRelatorio } from "pages/utils/abrirRelatorio";

import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";

import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import DialogEquipamentosEventos from "pages/equipamentos/DialogEquipamentoEventos";
import DialogEquipamentoFicheiros from "pages/equipamentos/DialogEquipamentoFicheiros";

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-eq-ficheiros-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function EquipamentoThumb({ id, tick }) {
  const [ok, setOk] = useState(true);
  const urlFull = `/Uploads/Equipamentos/${id}/Imagens/1.jpg`;
  const urlThumb = `/Uploads/Equipamentos/${id}/Imagens/1_thumb.jpg`;

  // sempre que fizeres refresh (tick), tentamos outra vez
  useEffect(() => {
    setOk(true);
  }, [tick, id]);

  if (!id) return null;

  // Se falhou, não mostramos nada e não há link
  if (!ok) return null;

  return (
    <a
      href={urlFull}
      target="_blank"
      rel="noreferrer"
      title="Abrir imagem"
      style={{ display: "inline-flex", alignItems: "center" }}
    >
      <img
        src={tick ? `${urlThumb}?t=${tick}` : urlThumb}
        loading="lazy"
        decoding="async"
        alt=""
        style={{
          width: 46,
          height: 36,
          objectFit: "cover",
          borderRadius: 6,
          border: "1px solid rgba(0,0,0,0.15)",
          display: "block",
        }}
        onError={() => setOk(false)}
      />
    </a>
  );
}

// ------------------- Filtros por coluna -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter((e.target.value ?? "").toUpperCase() || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function SimNaoFilter({ column: { filterValue, setFilter } }) {
  return (
    <select
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    >
      <option value="">Todos</option>
      <option value="1">Sim</option>
      <option value="0">Não</option>
    </select>
  );
}

function formatMatriculaSe6(v) {
  const original = (v || "").trim().toUpperCase();
  const raw = original.replace(/[^A-Z0-9]/g, "");

  // só aplica o formato 2-2-2 quando tiver exatamente 6 caracteres
  if (raw.length === 6) {
    return `${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4, 6)}`;
  }

  return original; // não mexe no resto
}

// ------------------- Persistência de filtros -------------------
const FILTERS_KEY = "tbl:equipamentos:filters";
const KEEP_KEY = "tbl:equipamentos:keep";

export default function Equipamentos() {
  const { showAlert } = useAlert();
  const { temPermissao, userData } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingDua, setLoadingDua] = useState(false);

  const [idEventoAbrir, setIdEventoAbrir] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const pendingOpenRef = useRef(null);

  const [openFicheiros, setOpenFicheiros] = useState(false);
  const [idEquipFicheiros, setIdEquipFicheiros] = useState(0);
  const [nomeEquipFicheiros, setNomeEquipFicheiros] = useState("");

  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;

  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const [data, setData] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [utilizadores, setUtilizadores] = useState([]);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = async () => {
    setLoading(true);
    // Utilizadores (para Responsável)
    const [payloadUsers, errUsers] = await handleApi(
      api.post("Equipamentos/SelResponsaveisEquipamentos", { mostrar_inativos: 0 })
    );
    if (errUsers) showAlert(errUsers, "error");

    const usersArr = Array.isArray(payloadUsers)
      ? payloadUsers
      : Array.isArray(payloadUsers?.data)
      ? payloadUsers.data
      : [];

    setUtilizadores(usersArr);

    // Tipos
    const [payloadTipos, errTipos] = await handleApi(
      api.post("Equipamentos/SelEquipamentosTipos", { mostrar_inativos: 1 })
    );
    if (errTipos) showAlert(errTipos, "error");

    // Equipamentos
    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentos", { mostrar_inativos: 1 })
    );
    if (err) showAlert(err, "error");

    const tiposArr = Array.isArray(payloadTipos)
      ? payloadTipos
      : Array.isArray(payloadTipos?.data)
      ? payloadTipos.data
      : [];

    setTipos(tiposArr);

    console.log("tiposArr", tiposArr);

    const dataArr = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
      ? payload.data
      : [];

    setData(dataArr);

    console.log("dataArr", dataArr);
    console.log("[carregar] tiposArr len =", tiposArr.length, tiposArr);
    console.log("[carregar] dataArr len =", dataArr.length);

    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      await carregar();
    })();
  }, []);

  // ------------------- Dialogs -------------------
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [openEventos, setOpenEventos] = useState(false);
  const [equipamentoEventos, setEquipamentoEventos] = useState(null);

  const [duaTokenNovo, setDuaTokenNovo] = useState(null);
  const [duaTokenSel, setDuaTokenSel] = useState(null);

  const emptyForm = {
    nome_curto: "",
    id_tipo: 0,
    ref_interna: "",
    ref_externa: "",
    numero_serie: "",
    matricula: "",
    matricula_data: "",
    marca: "",
    modelo_e_versao: "",
    cor: "",
    tipo_alimentacao: "",
    tipo_gas: "",
    carga_gas_kg: "",
    data_aquisicao: "",
    garantia1_data_limite: "",
    garantia1_obs: "",
    garantia2_data_limite: "",
    garantia2_obs: "",
    estado: "Ativo",
    data_abate: "",
    obs_estado: "",
    localizacao: "",
    id_responsavel: "",

    // --- Campos extra (Viatura) ---
    peso_bruto_kg: "",
    tara_kg: "",
    cilindrada_cm3: "",
    potencia_max_kw: "",
    lugares: "",
    categoria: "",
    tipo_veiculo: "",
    tipo_caixa: "",
    pneus_frente: "",
    pneus_tras: "",
    comprimento_caixa_carga_mm: "",

    inativo: 0,
  };

  const handleOpenAdd = () => {
    setNovo({ ...emptyForm });
    setOpenAdd(true);
    setDuaOcrNovo(null);
    setDuaTokenNovo(null);
  };

  const handleCloseAdd = () => {
    setNovo({ ...emptyForm });
    setOpenAdd(false);
    setDuaOcrNovo(null);
    setDuaTokenNovo(null);
  };

  const [novo, setNovo] = useState({ ...emptyForm });
  const [sel, setSel] = useState(null);

  const [imgTick, setImgTick] = useState(() => Date.now());
  // ✅ cache-bust por equipamento (para não refrescar todas as miniaturas da tabela)
  const [imgTickById, setImgTickById] = useState({});

  const [img1Exists, setImg1Exists] = useState(true);
  const [img2Exists, setImg2Exists] = useState(true);
  const [imgMenu, setImgMenu] = useState(null); // { anchorEl, idx: 1|2 } | null
  const [canCamera, setCanCamera] = useState(false);

  useEffect(() => {
    try {
      const ua = String(navigator.userAgent || "");
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      setCanCamera(isMobile);
    } catch {
      setCanCamera(false);
    }
  }, []);

  useEffect(() => {
    setImgTick(Date.now());
  }, []);

  // ------------------- DUA OCR -------------------
  const [duaOcrNovo, setDuaOcrNovo] = useState(null);
  const [duaOcrSel, setDuaOcrSel] = useState(null);

  const normTipoAlimentacao = (s) => {
    const t = String(s ?? "")
      .trim()
      .toUpperCase();
    if (t === "ELECTRICO" || t === "ELETRICO") return "ELÉTRICO";
    return t;
  };

  const buildModeloVersao = (d3, d2) => {
    const a = String(d3 ?? "").trim();
    const b = String(d2 ?? "").trim();
    if (a && b) return `${a} ${b}`;
    return a || b;
  };

  const compressImageIfNeeded = async (file) => {
    try {
      if (!file) return file;

      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();

      const isImg =
        type.startsWith("image/") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".png") ||
        name.endsWith(".webp");

      if (!isImg) return file;

      // Se já é pequeno, não mexer
      if (file.size <= 350 * 1024) return file; // <= 350 KB

      const MAX_DIM = 1600;
      const QUALITY = 0.75;

      let bitmap;
      if ("createImageBitmap" in window) {
        bitmap = await createImageBitmap(file);
      } else {
        bitmap = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      }

      const w = bitmap.width;
      const h = bitmap.height;

      let nw = w;
      let nh = h;

      if (w >= h && w > MAX_DIM) {
        nw = MAX_DIM;
        nh = Math.round((h * MAX_DIM) / w);
      } else if (h > w && h > MAX_DIM) {
        nh = MAX_DIM;
        nw = Math.round((w * MAX_DIM) / h);
      }

      const canvas = document.createElement("canvas");
      canvas.width = nw;
      canvas.height = nh;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(bitmap, 0, 0, nw, nh);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
      if (!blob) return file;

      const base = file.name?.replace(/\.[^/.]+$/, "") || "imagem";
      const newName = `${base}.jpg`;

      const compressed = new File([blob], newName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      if (compressed.size >= file.size) return file;

      return compressed;
    } catch {
      return file;
    }
  };

  const aplicarDuaOcrNoForm = (ocr, setter) => {
    if (!ocr) return;

    let pesoBruto = null;
    if (ocr.f2_massa_bruto_kg !== null && ocr.f2_massa_bruto_kg !== undefined) {
      const n = parseInt(String(ocr.f2_massa_bruto_kg).replace(/[^\d]/g, ""), 10);
      if (!Number.isNaN(n) && n > 0) pesoBruto = n;
    }

    setter((p) => {
      const prev = p || {}; // ✅ aqui é que está a proteção a sério

      const mv = buildModeloVersao(ocr.d3_modelo, ocr.d2_tipo_variante_versao);
      return {
        ...prev,

        // topo
        marca: ocr.d1_marca ? String(ocr.d1_marca).toUpperCase() : prev.marca ?? "",
        modelo_e_versao: mv ? mv.toUpperCase() : prev.modelo_e_versao ?? "",
        numero_serie: ocr.e_vin ? String(ocr.e_vin).toUpperCase() : prev.numero_serie ?? "",

        // viatura
        tara_kg: ocr.g_tara_kg ?? prev.tara_kg,
        cilindrada_cm3: ocr.p1_cilindrada_cm3 ?? prev.cilindrada_cm3,
        potencia_max_kw: ocr.p2_potencia_kw ?? prev.potencia_max_kw,
        tipo_alimentacao: ocr.p3_tipo_alimentacao
          ? normTipoAlimentacao(ocr.p3_tipo_alimentacao)
          : prev.tipo_alimentacao ?? "",

        categoria: ocr.j1_categoria ? String(ocr.j1_categoria).toUpperCase() : prev.categoria ?? "",
        tipo_veiculo: ocr.j2_tipo_veiculo
          ? String(ocr.j2_tipo_veiculo).toUpperCase()
          : prev.tipo_veiculo ?? "",
        tipo_caixa: ocr.j3_tipo_caixa
          ? String(ocr.j3_tipo_caixa).toUpperCase()
          : prev.tipo_caixa ?? "",

        pneus_frente: ocr.x1_pneus_frente
          ? String(ocr.x1_pneus_frente).toUpperCase()
          : prev.pneus_frente ?? "",
        pneus_tras: ocr.x2_pneus_tras
          ? String(ocr.x2_pneus_tras).toUpperCase()
          : prev.pneus_tras ?? "",

        peso_bruto_kg: pesoBruto ?? prev.peso_bruto_kg,
      };
    });
  };

  const lerDuaPorOcr = async ({ file, targetForm }) => {
    if (!file) return;

    const fd = new FormData();
    fd.append("file", file);

    setLoadingDua(true);
    const [ocr, err, meta] = await handleApi(
      api.post("Equipamentos/OcrDua", fd, { headers: { "Content-Type": "multipart/form-data" } })
    );
    setLoadingDua(false);

    if (err) return showAlert(err, "error");
    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    // 1) normaliza a resposta
    const tokenOcr = ocr?.data?.token ?? ocr?.token ?? null;
    const dadosOcr = ocr?.data?.ocr ?? ocr?.ocr ?? ocr?.data ?? ocr;

    console.log("[DUA OCR] token=", tokenOcr, "dadosOcr=", dadosOcr);

    // 2) grava token + dados + aplica ao form
    if (targetForm === "novo") {
      setDuaTokenNovo(tokenOcr);
      setDuaOcrNovo(dadosOcr);
      aplicarDuaOcrNoForm(dadosOcr, setNovo);
    } else {
      setDuaTokenSel(tokenOcr);
      setDuaOcrSel(dadosOcr);
      aplicarDuaOcrNoForm(dadosOcr, setSel);
    }
  };

  const inserir = async () => {
    if (!temPermissao("Menu Equipamentos", "w")) return;

    const nome = (novo.nome_curto ?? "").trim().toUpperCase();

    if (!nome) return showAlert("Nome curto obrigatório.", "warning");
    if (!novo.id_tipo || Number(novo.id_tipo) <= 0)
      return showAlert("Tipo obrigatório.", "warning");

    setLoading(true);
    const body = {
      ...novo,
      nome_curto: nome,
      id_tipo: Number(novo.id_tipo),
      carga_gas_kg: novo.carga_gas_kg === "" ? null : Number(novo.carga_gas_kg),
      id_responsavel:
        novo.id_responsavel === "" ||
        novo.id_responsavel === null ||
        novo.id_responsavel === undefined
          ? null
          : Number(novo.id_responsavel),

      matricula_data: novo.matricula_data || null,

      peso_bruto_kg: novo.peso_bruto_kg === "" ? null : Number(novo.peso_bruto_kg),
      tara_kg: novo.tara_kg === "" ? null : Number(novo.tara_kg),
      cilindrada_cm3: novo.cilindrada_cm3 === "" ? null : Number(novo.cilindrada_cm3),
      potencia_max_kw: novo.potencia_max_kw === "" ? null : Number(novo.potencia_max_kw),
      lugares: novo.lugares === "" ? null : Number(novo.lugares),
      comprimento_caixa_carga_mm:
        novo.comprimento_caixa_carga_mm === "" ? null : Number(novo.comprimento_caixa_carga_mm),

      data_aquisicao: novo.data_aquisicao || null,
      garantia1_data_limite: novo.garantia1_data_limite || null,
      garantia2_data_limite: novo.garantia2_data_limite || null,
      data_abate: novo.data_abate || null,

      dua_temp_token: duaTokenNovo,
    };

    const [_, err, meta] = await handleApi(api.post("Equipamentos/InsEquipamento", body));
    if (err) showAlert(err, "error");
    else showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();
    setOpenAdd(false);
    setNovo({ ...emptyForm });
    setDuaTokenNovo(null);
    setLoading(false);
  };

  const uploadImagemEquipamento = async (idEquipamento, slot, file) => {
    if (!idEquipamento || idEquipamento <= 0) return;
    if (!file) return;

    setLoading(true);

    const fileFinal = await compressImageIfNeeded(file);

    const fd = new FormData();
    fd.append("id", String(idEquipamento));
    fd.append("slot", String(slot));
    fd.append("file", fileFinal);

    setLoading(true);
    const [_, err, meta] = await handleApi(
      api.post("Equipamentos/UploadEquipamentoImagem", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
    setLoading(false);

    if (err) showAlert(err, "error");
    else showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    const t = Date.now();

    // ✅ refresca os previews grandes (no dialog editar)
    setImgTick(t);

    // ✅ refresca só a miniatura deste equipamento na tabela
    setImgTickById((prev) => ({ ...(prev || {}), [idEquipamento]: t }));
  };

  const gravarEdicao = async () => {
    if (!temPermissao("Menu Equipamentos", "u")) return;
    if (!sel?.id) return;

    const nome = (sel.nome_curto ?? "").trim().toUpperCase();

    if (!nome) return showAlert("Nome curto obrigatório.", "warning");
    if (!sel.id_tipo || Number(sel.id_tipo) <= 0) return showAlert("Tipo obrigatório.", "warning");

    setLoading(true);

    const body = {
      ...sel,
      id: sel.id,
      nome_curto: nome,
      id_tipo: Number(sel.id_tipo),
      carga_gas_kg: sel.carga_gas_kg === "" ? null : Number(sel.carga_gas_kg),
      id_responsavel:
        sel.id_responsavel === "" || sel.id_responsavel === null || sel.id_responsavel === undefined
          ? null
          : Number(sel.id_responsavel),

      matricula_data: sel.matricula_data || null,

      peso_bruto_kg: sel.peso_bruto_kg === "" ? null : Number(sel.peso_bruto_kg),
      tara_kg: sel.tara_kg === "" ? null : Number(sel.tara_kg),
      cilindrada_cm3: sel.cilindrada_cm3 === "" ? null : Number(sel.cilindrada_cm3),
      potencia_max_kw: sel.potencia_max_kw === "" ? null : Number(sel.potencia_max_kw),
      lugares: sel.lugares === "" ? null : Number(sel.lugares),
      comprimento_caixa_carga_mm:
        sel.comprimento_caixa_carga_mm === "" ? null : Number(sel.comprimento_caixa_carga_mm),

      data_aquisicao: sel.data_aquisicao || null,
      garantia1_data_limite: sel.garantia1_data_limite || null,
      garantia2_data_limite: sel.garantia2_data_limite || null,
      data_abate: sel.data_abate || null,
      inativo: sel.inativo === 1 ? 1 : 0,
    };

    const [_, err, meta] = await handleApi(api.post("Equipamentos/UpdEquipamento", body));
    if (err) showAlert(err, "error");
    else showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();
    setOpenEdit(false);
    setLoading(false);
  };

  const abrirRelatorioEquipamento = async (id) => {
    if (!id || Number(id) <= 0) return;

    setLoading(true);

    // console.log("PDF click equipamento id =", id);

    const [payload, err, meta] = await handleApi(
      api.post("Relatorios/ObterRptEquipamentoEventos", {
        id_equipamento: Number(id),
        tipo: "pdf",
      })
    );

    setLoading(false);

    // console.log("PDF payload =", payload, "err =", err, "meta =", meta);

    if (err) {
      showAlert(err, "error");
      return;
    }

    // opcional (mas consistente com o resto do ficheiro)
    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    // tenta apanhar a URL de várias formas (depende do que o backend devolve)
    const url = payload?.url ?? payload?.data?.url ?? payload?.data ?? payload;
    if (!url) {
      showAlert(meta?.message || "Não foi possível obter o URL do relatório.", "error");
      return;
    }

    abrirRelatorio(url);
  };

  // ------------------- Tabela -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 150,
        minWidth: 150,
        maxWidth: 150,
        disableSortBy: true,
        filterable: false,
        sortable: false,
        hideable: false,
        resizable: false,
        Cell: ({ row }) => {
          const e = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <IconButton
                size="small"
                color="info"
                title="Ficha do Equipamento"
                disabled={loading}
                onClick={() => abrirRelatorioEquipamento(row.original.id)}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="primary"
                size="small"
                disabled={!temPermissao("Menu Equipamentos", "u")}
                onClick={() => {
                  setSel({
                    ...e,

                    // strings "simples" (opcional, mas coerente)
                    matricula: e.matricula ?? "",

                    // ids/números
                    id_tipo: e.id_tipo ?? 0,
                    carga_gas_kg: e.carga_gas_kg ?? "",
                    id_responsavel: e.id_responsavel ?? "",

                    // datas (IMPORTANTE)
                    data_aquisicao: e.data_aquisicao ? String(e.data_aquisicao).slice(0, 10) : "",
                    matricula_data: e.matricula_data ? String(e.matricula_data).slice(0, 10) : "",
                    garantia1_data_limite: e.garantia1_data_limite
                      ? String(e.garantia1_data_limite).slice(0, 10)
                      : "",
                    garantia2_data_limite: e.garantia2_data_limite
                      ? String(e.garantia2_data_limite).slice(0, 10)
                      : "",
                    data_abate: e.data_abate ? String(e.data_abate).slice(0, 10) : "",

                    // extras (se estiverem no backend)
                    peso_bruto_kg: e.peso_bruto_kg ?? "",
                    tara_kg: e.tara_kg ?? "",
                    cilindrada_cm3: e.cilindrada_cm3 ?? "",
                    potencia_max_kw: e.potencia_max_kw ?? "",
                    lugares: e.lugares ?? "",
                    categoria: e.categoria ?? "",
                    tipo_veiculo: e.tipo_veiculo ?? "",
                    tipo_caixa: e.tipo_caixa ?? "",
                    pneus_frente: e.pneus_frente ?? "",
                    pneus_tras: e.pneus_tras ?? "",
                    comprimento_caixa_carga_mm: e.comprimento_caixa_carga_mm ?? "",
                  });

                  setDuaOcrSel(null);
                  setOpenEdit(true);

                  setImg1Exists(true);
                  setImg2Exists(true);
                }}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                color="info"
                size="small"
                disabled={!temPermissao("Menu Equipamentos", "r")}
                onClick={() => {
                  setEquipamentoEventos(e);
                  setOpenEventos(true);
                }}
                title="Eventos"
              >
                <EventNoteIcon fontSize="small" />
              </IconButton>

              <IconButton
                color="secondary"
                size="small"
                disabled={!temPermissao("Menu Equipamentos", "r")}
                onClick={() => {
                  setIdEquipFicheiros(e.id);
                  setNomeEquipFicheiros(e?.nome_curto || "");
                  setOpenFicheiros(true);
                }}
                title="Ficheiros"
              >
                <FolderOpenIcon color="info" fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },

      {
        Header: "Imagem",
        id: "imagem",
        accessor: (row) => row.id,
        disableFilters: true,
        disableSortBy: true,
        filterable: false,
        sortable: false,
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        align: "center",
        Cell: ({ value }) => {
          const idEquipamento = Number(value || 0);
          const tickRow = imgTickById?.[idEquipamento] || 0;

          return <EquipamentoThumb id={idEquipamento} tick={tickRow} />;
        },
      },

      { Header: "Matrícula", accessor: "matricula", Filter: TextFilter },

      {
        Header: "Nome",
        accessor: "nome_curto",
        Filter: TextFilter,
        Cell: ({ row, value }) => {
          const temEventosFuturos = Number(row?.original?.tem_eventos_futuros ?? 0) === 1;

          return (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              {!temEventosFuturos && (
                <Tooltip title="Sem eventos futuros marcados">
                  <NotificationImportantIcon sx={{ color: "#f57c00" }} fontSize="small" />
                </Tooltip>
              )}
              <span>{value}</span>
            </MDBox>
          );
        },
      },
      { Header: "Tipo", accessor: "tipo_nome_curto", Filter: TextFilter },

      { Header: "Nº Série / VIN", accessor: "numero_serie", Filter: TextFilter },

      { Header: "Marca", accessor: "marca", Filter: TextFilter },
      { Header: "Modelo / Versão", accessor: "modelo_e_versao", Filter: TextFilter },
      { Header: "Cor", accessor: "cor", Filter: TextFilter },
      { Header: "Responsável", accessor: "responsavel", Filter: TextFilter },
      { Header: "Ref. Interna", accessor: "ref_interna", Filter: TextFilter },
      { Header: "Ref. Externa", accessor: "ref_externa", Filter: TextFilter },
      {
        Header: "Ativo",
        accessor: "inativo",
        width: 90,
        minWidth: 90,
        align: "center",
        filterType: "simNaoInverso",
        Cell: ({ value }) => (Number(value || 0) === 1 ? "Não" : "Sim"),
      },
    ],
    [temPermissao, imgTickById]
  );

  const dataFiltrada = useMemo(() => {
    if (mostrarInativos) return data;
    return (data || []).filter((e) => Number(e?.inativo ?? 0) === 0);
  }, [data, mostrarInativos]);

  const table = useTable(
    {
      columns,
      data: dataFiltrada,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  const OPCOES_TIPO_ALIMENTACAO = [
    "ELÉTRICO",
    "GASÓLEO",
    "GASOLINA",
    "HÍBRIDO PLUG-IN",
    "HÍBRIDO",
    "GPL",
    "GÁS",
    "OUTRO",
  ];

  const OPCOES_TIPO_GAS = ["R134A", "R1234YF", "R410A", "R32", "CO2", "OUTRO"];

  // ------------------- Helpers: tipo selecionado / viatura -------------------
  const getTipoById = (id) => (tipos || []).find((t) => Number(t?.id) === Number(id)) || null;

  // Ajusta a regra conforme o teu backend:
  // 1) se existir campo boolean/int (ex: t.is_viatura ou t.viatura), usa isso
  // 2) fallback por nome (ex: "VIATURA")
  const isViaturaByTipo = (t) => {
    if (!t) return false;

    // tenta campos típicos (caso existam)
    if (t.is_viatura === 1 || t.is_viatura === true) return true;
    if (t.viatura === 1 || t.viatura === true) return true;

    // fallback por nome
    const nome = String(t.nome_curto ?? t.designacao ?? "").toUpperCase();

    return nome.includes("VIATUR") || nome.includes("EMPILHAD");
  };

  const tipoNovo = getTipoById(novo?.id_tipo);
  const tipoSel = getTipoById(sel?.id_tipo);

  const isViaturaNovo = isViaturaByTipo(tipoNovo);
  const isViaturaSel = isViaturaByTipo(tipoSel);

  const getLabelResponsavel = (opt) => {
    const nome = String(opt?.designacao ?? "");
    const fun = String(opt?.funcoes_interesse ?? "").trim();
    return fun ? `${nome} (${fun})` : nome;
  };

  const inputGhostStyle = {
    position: "absolute",
    left: "-9999px",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
  };

  useEffect(() => {
    const st = location?.state;
    const idEq = Number(st?.openEquipamentoId || 0);
    const idEv = Number(st?.openEventoId || 0);

    if (idEq > 0 && idEv > 0) {
      pendingOpenRef.current = { idEq, idEv };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  useEffect(() => {
    const pend = pendingOpenRef.current;
    if (!pend) return;

    // só avançamos quando já temos lista de equipamentos
    if (!Array.isArray(data) || data.length === 0) return;

    const idEq = Number(pend.idEq || 0);
    const idEv = Number(pend.idEv || 0);
    if (idEq <= 0 || idEv <= 0) {
      pendingOpenRef.current = null;
      return;
    }

    const eq = data.find((x) => Number(x?.id) === idEq);
    if (!eq) {
      pendingOpenRef.current = null;
      return;
    }

    // abre dialog de eventos do equipamento e "aponta" ao evento
    setEquipamentoEventos(eq);
    setIdEventoAbrir(idEv);
    setOpenEventos(true);

    // limpa para não repetir ao re-render
    pendingOpenRef.current = null;

    // opcional: limpa o state da rota (para não reabrir ao voltar atrás)
    try {
      navigate(location.pathname, { replace: true, state: {} });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location.key]);

  // ------------------- UI -------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* PROGRESS GLOBAL — sempre aqui, logo após o navbar */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              <MDTypography variant="h6">Equipamentos</MDTypography>

              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={mostrarInativos}
                      onChange={(e) => setMostrarInativos(e.target.checked)}
                      color="info"
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>Mostrar inativos</span>}
                  sx={{ marginRight: 2 }}
                />

                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Equipamentos", "w")}
                  onClick={handleOpenAdd}
                >
                  Novo Equipamento
                </MDButton>
              </MDBox>
            </MDBox>

            <ProjectTable
              tableKey="equipamentos"
              version="v2"
              columns={columns}
              data={dataFiltrada}
              loading={loading}
              minWidth={1700}
              maxHeight="68vh"
              emptyMessage="Sem equipamentos para apresentar."
              showKeepFilters
            />
          </MDBox>
        </Card>
      </MDBox>

      {/* ADICIONAR */}
      <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Novo Equipamento
        </DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput
              label="Nome curto *"
              value={novo.nome_curto}
              onChange={(e) =>
                setNovo((p) => ({ ...p, nome_curto: (e.target.value ?? "").toUpperCase() }))
              }
              fullWidth
            />

            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Tipo *
              </MDTypography>
              <select
                value={novo.id_tipo}
                onChange={(e) => {
                  const idTipo = Number(e.target.value);
                  const ehViatura = isViaturaByTipo(getTipoById(idTipo));

                  setNovo((p) => ({
                    ...p,
                    id_tipo: idTipo,
                    ...(ehViatura
                      ? {}
                      : {
                          peso_bruto_kg: "",
                          tara_kg: "",
                          cilindrada_cm3: "",
                          potencia_max_kw: "",
                          lugares: "",
                          categoria: "",
                          tipo_veiculo: "",
                          tipo_caixa: "",
                          pneus_frente: "",
                          pneus_tras: "",
                          comprimento_caixa_carga_mm: "",
                        }),
                  }));

                  // 🔥 se deixou de ser viatura, limpa o OCR
                  if (!ehViatura) setDuaOcrNovo(null);
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.25)",
                  fontSize: 14,
                  outline: "none",
                }}
              >
                <option value={0}>— selecionar —</option>
                {tipos
                  .filter((t) => t?.inativo !== 1)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome_curto}
                    </option>
                  ))}
              </select>
            </div>

            {isViaturaNovo && (
              <MDBox
                sx={{
                  position: "relative",
                  border: "1px dashed rgba(0,0,0,0.35)",
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                {loadingDua && (
                  <MDBox
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.65)",
                      zIndex: 5,
                      borderRadius: 2,
                    }}
                  >
                    <CircularProgress />
                  </MDBox>
                )}
                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <MDTypography variant="button">
                    Certificado de Matrícula (cartão novo)
                  </MDTypography>

                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton variant="contained" color="info" component="label">
                      <UploadFileIcon sx={{ mr: 0.75 }} fontSize="small" />
                      Ficheiro
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) lerDuaPorOcr({ file: f, targetForm: "novo" });
                        }}
                      />
                    </MDButton>

                    {canCamera && (
                      <MDButton variant="contained" color="info" component="label">
                        <PhotoCameraIcon sx={{ mr: 0.75 }} fontSize="small" />
                        Câmera
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={inputGhostStyle}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) lerDuaPorOcr({ file: f, targetForm: "novo" });
                          }}
                        />
                      </MDButton>
                    )}
                  </MDBox>
                </MDBox>

                {!!duaOcrNovo && (
                  <MDBox mt={1}>
                    <MDTypography
                      variant="caption"
                      sx={{ display: "block", color: "rgba(0,0,0,0.7)" }}
                    >
                      Extraído:{" "}
                      {[
                        duaOcrNovo?.d1_marca ? `Marca=${duaOcrNovo.d1_marca}` : null,
                        duaOcrNovo?.d3_modelo ? `Modelo=${duaOcrNovo.d3_modelo}` : null,
                        duaOcrNovo?.d2_tipo_variante_versao
                          ? `D.2=${duaOcrNovo.d2_tipo_variante_versao}`
                          : null,
                        duaOcrNovo?.e_vin ? `VIN=${duaOcrNovo.e_vin}` : null,
                        duaOcrNovo?.g_tara_kg ? `Tara=${duaOcrNovo.g_tara_kg}` : null,
                        duaOcrNovo?.p2_potencia_kw ? `kW=${duaOcrNovo.p2_potencia_kw}` : null,
                        duaOcrNovo?.p3_tipo_alimentacao
                          ? `Alim=${duaOcrNovo.p3_tipo_alimentacao}`
                          : null,
                        duaOcrNovo?.x1_pneus_frente ? `PneusF=${duaOcrNovo.x1_pneus_frente}` : null,
                        duaOcrNovo?.x2_pneus_tras ? `PneusT=${duaOcrNovo.x2_pneus_tras}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <MDInput
                label="Ref. interna"
                value={novo.ref_interna}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, ref_interna: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Ref. externa"
                value={novo.ref_externa}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, ref_externa: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Nº Série / VIN"
                value={novo.numero_serie}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, numero_serie: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />

              <MDInput
                label="Marca"
                value={novo.marca}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, marca: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Modelo / Versão"
                value={novo.modelo_e_versao || ""}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, modelo_e_versao: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Cor"
                value={novo.cor || ""}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, cor: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Localização"
                value={novo.localizacao}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, localizacao: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
            </MDBox>

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Fonte de energia
                </MDTypography>
                <Autocomplete
                  options={OPCOES_TIPO_ALIMENTACAO}
                  value={novo.tipo_alimentacao || ""}
                  onChange={(_, v) => setNovo((p) => ({ ...p, tipo_alimentacao: v || "" }))}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} placeholder="—" size="small" fullWidth />
                  )}
                />
              </div>

              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Tipo gás
                </MDTypography>
                <Autocomplete
                  options={OPCOES_TIPO_GAS}
                  value={novo.tipo_gas || ""}
                  onChange={(_, v) => setNovo((p) => ({ ...p, tipo_gas: v || "" }))}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} placeholder="—" size="small" fullWidth />
                  )}
                />
              </div>

              <MDInput
                label="Carga gás (Kg)"
                type="number"
                value={novo.carga_gas_kg ?? ""}
                onChange={(e) => setNovo((p) => ({ ...p, carga_gas_kg: e.target.value }))}
                fullWidth
              />

              <TextField
                label="Data aquisição"
                type="date"
                size="medium"
                fullWidth
                value={novo.data_aquisicao || ""}
                onChange={(e) => setNovo((p) => ({ ...p, data_aquisicao: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>

            {/* GARANTIAS (no fim, em blocos) */}
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <MDBox
                sx={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Garantia 1
                </MDTypography>

                <MDBox display="grid" gap={1}>
                  <TextField
                    label="Data limite"
                    type="date"
                    size="medium"
                    fullWidth
                    value={novo.garantia1_data_limite || ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, garantia1_data_limite: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDInput
                    label="Obs"
                    value={novo.garantia1_obs || ""}
                    onChange={(e) =>
                      setNovo((p) => ({
                        ...p,
                        garantia1_obs: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>

              <MDBox
                sx={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Garantia 2
                </MDTypography>

                <MDBox display="grid" gap={1}>
                  <TextField
                    label="Data limite"
                    type="date"
                    size="medium"
                    fullWidth
                    value={novo.garantia2_data_limite || ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, garantia2_data_limite: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDInput
                    label="Obs"
                    value={novo.garantia2_obs || ""}
                    onChange={(e) =>
                      setNovo((p) => ({
                        ...p,
                        garantia2_obs: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>
            </MDBox>

            {isViaturaNovo && (
              <MDBox
                sx={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 2,
                  p: 1.25,
                  mt: 1,
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Campos Viatura
                </MDTypography>

                <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
                  <MDInput
                    label="Matrícula (A)"
                    value={novo.matricula}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, matricula: (e.target.value ?? "").toUpperCase() }))
                    }
                    onBlur={(e) =>
                      setNovo((p) => ({ ...p, matricula: formatMatriculaSe6(e.target.value) }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Data matrícula (B)"
                    type="date"
                    size="medium"
                    fullWidth
                    value={novo.matricula_data || ""}
                    onChange={(e) => setNovo((p) => ({ ...p, matricula_data: e.target.value }))}
                    InputLabelProps={{ shrink: true }} // essencial em inputs date
                  />

                  <MDInput
                    label="Peso bruto (F.1/F.2 X)"
                    type="number"
                    value={novo.peso_bruto_kg ?? ""}
                    onChange={(e) => setNovo((p) => ({ ...p, peso_bruto_kg: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Tara (G)"
                    type="number"
                    value={novo.tara_kg ?? ""}
                    onChange={(e) => setNovo((p) => ({ ...p, tara_kg: e.target.value }))}
                    fullWidth
                  />

                  <MDInput
                    label="Cilindrada (P.1)"
                    type="number"
                    value={novo.cilindrada_cm3 ?? ""}
                    onChange={(e) => setNovo((p) => ({ ...p, cilindrada_cm3: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Potência máx (kW) (P.2)"
                    type="number"
                    value={novo.potencia_max_kw ?? ""}
                    onChange={(e) => setNovo((p) => ({ ...p, potencia_max_kw: e.target.value }))}
                    fullWidth
                  />

                  <MDInput
                    label="Lugares (S.1)"
                    type="number"
                    value={novo.lugares ?? ""}
                    onChange={(e) => setNovo((p) => ({ ...p, lugares: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Categoria (J.1)"
                    value={novo.categoria ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, categoria: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Tipo veículo (J.2)"
                    value={novo.tipo_veiculo ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({
                        ...p,
                        tipo_veiculo: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                  <MDInput
                    label="Tipo caixa (J.3)"
                    value={novo.tipo_caixa ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, tipo_caixa: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Pneus frente (X.1)"
                    value={novo.pneus_frente ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({
                        ...p,
                        pneus_frente: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                  <MDInput
                    label="Pneus trás (X.2)"
                    value={novo.pneus_tras ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, pneus_tras: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Comprimento caixa carga (mm)"
                    type="number"
                    value={novo.comprimento_caixa_carga_mm ?? ""}
                    onChange={(e) =>
                      setNovo((p) => ({ ...p, comprimento_caixa_carga_mm: e.target.value }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>
            )}

            {/* ESTADO + OBS (em bloco) */}
            <MDBox
              sx={{
                border: "1px solid rgba(0,0,0,0.15)",
                borderRadius: 2,
                p: 1.25,
              }}
            >
              <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                Estado
              </MDTypography>

              <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
                <MDInput
                  label="Estado"
                  value={novo.estado || ""}
                  onChange={(e) =>
                    setNovo((p) => ({ ...p, estado: (e.target.value ?? "").toUpperCase() }))
                  }
                  fullWidth
                />

                <TextField
                  label="Data abate"
                  type="date"
                  size="medium"
                  fullWidth
                  value={novo.data_abate || ""}
                  onChange={(e) => setNovo((p) => ({ ...p, data_abate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>

              <MDInput
                label="Obs. estado"
                value={novo.obs_estado || ""}
                onChange={(e) =>
                  setNovo((p) => ({ ...p, obs_estado: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 1 }}
              />
            </MDBox>

            {/* RESPONSÁVEL (Autocomplete) */}
            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Responsável
              </MDTypography>

              <Autocomplete
                options={utilizadores || []}
                value={
                  (utilizadores || []).find((u) => Number(u.id) === Number(novo.id_responsavel)) ||
                  null
                }
                onChange={(_, opt) => setNovo((p) => ({ ...p, id_responsavel: opt?.id ?? null }))}
                getOptionLabel={getLabelResponsavel}
                isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Selecionar..." size="small" fullWidth />
                )}
              />
            </div>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseAdd} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* EDITAR */}
      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setDuaOcrSel(null);
          setSel(null);
        }}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-eq-ficheiros-title" sx={{ cursor: "move" }}>
          Editar Equipamento
        </DialogTitle>
        <DialogContent>
          {sel?.id ? (
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
              {/* IMAGEM 1 */}
              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Imagem 1
                </MDTypography>

                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 160,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.15)",
                    overflow: "hidden",
                    background: "#f5f5f5",
                  }}
                >
                  <img
                    src={`/Uploads/Equipamentos/${sel.id}/Imagens/1.jpg?t=${imgTick}`}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: img1Exists ? "block" : "none",
                    }}
                    onLoad={() => setImg1Exists(true)}
                    onError={() => setImg1Exists(false)}
                  />

                  {img1Exists && (
                    <a
                      href={`/Uploads/Equipamentos/${sel.id}/Imagens/1.jpg`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ position: "absolute", inset: 0, display: "block" }}
                      title="Abrir imagem 1"
                    />
                  )}

                  {!img1Exists && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(0,0,0,0.45)",
                        fontSize: 14,
                      }}
                    >
                      Sem imagem
                    </MDBox>
                  )}

                  <IconButton
                    size="small"
                    onClick={(e) => setImgMenu({ anchorEl: e.currentTarget, idx: 1 })}
                    title="Alterar imagem 1"
                    sx={{
                      position: "absolute",
                      right: 10,
                      bottom: 10,
                      background: "rgba(255,255,255,0.95)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                      "&:hover": { background: "rgba(255,255,255,1)" },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              </div>

              {/* IMAGEM 2 */}
              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Imagem 2
                </MDTypography>

                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: 160,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.15)",
                    overflow: "hidden",
                    background: "#f5f5f5",
                  }}
                >
                  <img
                    src={`/Uploads/Equipamentos/${sel.id}/Imagens/2.jpg?t=${imgTick}`}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: img2Exists ? "block" : "none",
                    }}
                    onLoad={() => setImg2Exists(true)}
                    onError={() => setImg2Exists(false)}
                  />

                  {img2Exists && (
                    <a
                      href={`/Uploads/Equipamentos/${sel.id}/Imagens/2.jpg`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ position: "absolute", inset: 0, display: "block" }}
                      title="Abrir imagem 2"
                    />
                  )}

                  {!img2Exists && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(0,0,0,0.45)",
                        fontSize: 14,
                      }}
                    >
                      Sem imagem
                    </MDBox>
                  )}

                  <IconButton
                    size="small"
                    onClick={(e) => setImgMenu({ anchorEl: e.currentTarget, idx: 2 })}
                    title="Alterar imagem 2"
                    sx={{
                      position: "absolute",
                      right: 10,
                      bottom: 10,
                      background: "rgba(255,255,255,0.95)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                      "&:hover": { background: "rgba(255,255,255,1)" },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </MDBox>
              </div>

              {/* MENU (partilhado) */}
              <Menu
                anchorEl={imgMenu?.anchorEl}
                open={Boolean(imgMenu?.anchorEl)}
                onClose={() => setImgMenu(null)}
              >
                <MenuItem component="label">
                  <ListItemIcon>
                    <UploadFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Ficheiro</ListItemText>

                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      const idx = imgMenu?.idx;
                      setImgMenu(null);
                      if (f && sel?.id && idx) uploadImagemEquipamento(sel.id, idx, f);
                    }}
                  />
                </MenuItem>

                {canCamera && (
                  <MenuItem component="label">
                    <ListItemIcon>
                      <PhotoCameraIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Câmera</ListItemText>

                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={inputGhostStyle}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        const idx = imgMenu?.idx;
                        setImgMenu(null);
                        if (f && sel?.id && idx) uploadImagemEquipamento(sel.id, idx, f);
                      }}
                    />
                  </MenuItem>
                )}
              </Menu>
            </MDBox>
          ) : null}

          <MDBox display="grid" gap={1.25} mt={1.5}>
            <MDInput
              label="Nome curto *"
              value={sel?.nome_curto || ""}
              onChange={(e) =>
                setSel((p) => ({ ...p, nome_curto: (e.target.value ?? "").toUpperCase() }))
              }
              fullWidth
            />

            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Tipo *
              </MDTypography>
              <select
                value={sel?.id_tipo ?? 0}
                onChange={(e) => {
                  const idTipo = Number(e.target.value);
                  setSel((p) => ({
                    ...p,
                    id_tipo: idTipo,
                    ...(isViaturaByTipo(getTipoById(idTipo))
                      ? {}
                      : {
                          peso_bruto_kg: "",
                          tara_kg: "",
                          cilindrada_cm3: "",
                          potencia_max_kw: "",
                          lugares: "",
                          categoria: "",
                          tipo_veiculo: "",
                          tipo_caixa: "",
                          pneus_frente: "",
                          pneus_tras: "",
                          comprimento_caixa_carga_mm: "",
                          // opcional: se quiseres limpar também matrícula/datas quando deixa de ser viatura
                          // matricula: "",
                          // matricula_data: "",
                        }),
                  }));
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.25)",
                  fontSize: 14,
                  outline: "none",
                }}
              >
                <option value={0}>— selecionar —</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome_curto}
                  </option>
                ))}
              </select>
            </div>

            {isViaturaSel && (
              <MDBox
                sx={{
                  position: "relative",
                  border: "1px dashed rgba(0,0,0,0.35)",
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                {loadingDua && (
                  <MDBox
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.65)",
                      zIndex: 5,
                      borderRadius: 2,
                    }}
                  >
                    <CircularProgress />
                  </MDBox>
                )}
                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <MDTypography variant="button">DUA OCR (cartão)</MDTypography>

                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton variant="contained" color="info" component="label">
                      <UploadFileIcon sx={{ mr: 0.75 }} fontSize="small" />
                      Ficheiro
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) lerDuaPorOcr({ file: f, targetForm: "sel" });
                        }}
                      />
                    </MDButton>

                    {canCamera && (
                      <MDButton variant="contained" color="info" component="label">
                        <PhotoCameraIcon sx={{ mr: 0.75 }} fontSize="small" />
                        Câmera
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={inputGhostStyle}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) lerDuaPorOcr({ file: f, targetForm: "sel" });
                          }}
                        />
                      </MDButton>
                    )}
                  </MDBox>
                </MDBox>

                {!!duaOcrSel && (
                  <MDBox mt={1}>
                    <MDTypography
                      variant="caption"
                      sx={{ display: "block", color: "rgba(0,0,0,0.7)" }}
                    >
                      Extraído:{" "}
                      {[
                        duaOcrSel?.d1_marca ? `Marca=${duaOcrSel.d1_marca}` : null,
                        duaOcrSel?.d3_modelo ? `Modelo=${duaOcrSel.d3_modelo}` : null,
                        duaOcrSel?.d2_tipo_variante_versao
                          ? `D.2=${duaOcrSel.d2_tipo_variante_versao}`
                          : null,
                        duaOcrSel?.e_vin ? `VIN=${duaOcrSel.e_vin}` : null,
                        duaOcrSel?.g_tara_kg ? `Tara=${duaOcrSel.g_tara_kg}` : null,
                        duaOcrSel?.p2_potencia_kw ? `kW=${duaOcrSel.p2_potencia_kw}` : null,
                        duaOcrSel?.p3_tipo_alimentacao
                          ? `Alim=${duaOcrSel.p3_tipo_alimentacao}`
                          : null,
                        duaOcrSel?.x1_pneus_frente ? `PneusF=${duaOcrSel.x1_pneus_frente}` : null,
                        duaOcrSel?.x2_pneus_tras ? `PneusT=${duaOcrSel.x2_pneus_tras}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <MDInput
                label="Ref. interna"
                value={sel?.ref_interna || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, ref_interna: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Ref. externa"
                value={sel?.ref_externa || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, ref_externa: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Nº Série / VIN"
                value={sel?.numero_serie || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, numero_serie: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />

              <MDInput
                label="Marca (D.1)"
                value={sel?.marca || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, marca: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Modelo (D.3) / Versão (D.2)"
                value={sel?.modelo_e_versao || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, modelo_e_versao: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Cor (R)"
                value={sel?.cor || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, cor: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
              <MDInput
                label="Localização"
                value={sel?.localizacao || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, localizacao: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
              />
            </MDBox>

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Fonte de energia
                </MDTypography>
                <Autocomplete
                  options={OPCOES_TIPO_ALIMENTACAO}
                  value={sel?.tipo_alimentacao || ""}
                  onChange={(_, v) => setSel((p) => ({ ...p, tipo_alimentacao: v || "" }))}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} placeholder="—" size="small" fullWidth />
                  )}
                />
              </div>

              <div>
                <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                  Tipo gás
                </MDTypography>
                <Autocomplete
                  options={OPCOES_TIPO_GAS}
                  value={sel?.tipo_gas || ""}
                  onChange={(_, v) => setSel((p) => ({ ...p, tipo_gas: v || "" }))}
                  freeSolo
                  renderInput={(params) => (
                    <TextField {...params} placeholder="—" size="small" fullWidth />
                  )}
                />
              </div>

              <MDInput
                label="Carga gás (Kg)"
                type="number"
                value={sel?.carga_gas_kg ?? ""}
                onChange={(e) => setSel((p) => ({ ...p, carga_gas_kg: e.target.value }))}
                fullWidth
              />

              <TextField
                label="Data aquisição"
                type="date"
                size="medium"
                fullWidth
                value={sel?.data_aquisicao || ""}
                onChange={(e) => setSel((p) => ({ ...p, data_aquisicao: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>

            {/* GARANTIAS (no fim, em blocos) */}
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <MDBox sx={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: 2, p: 1.25 }}>
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Garantia 1
                </MDTypography>

                <MDBox display="grid" gap={1}>
                  <TextField
                    label="Data limite"
                    type="date"
                    size="medium"
                    fullWidth
                    value={sel?.garantia1_data_limite || ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, garantia1_data_limite: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDInput
                    label="Obs"
                    value={sel?.garantia1_obs || ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, garantia1_obs: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>

              <MDBox sx={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: 2, p: 1.25 }}>
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Garantia 2
                </MDTypography>

                <MDBox display="grid" gap={1}>
                  <TextField
                    label="Data limite"
                    type="date"
                    size="medium"
                    fullWidth
                    value={sel?.garantia2_data_limite || ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, garantia2_data_limite: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDInput
                    label="Obs"
                    value={sel?.garantia2_obs || ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, garantia2_obs: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>
            </MDBox>

            {isViaturaSel && (
              <MDBox
                sx={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 2,
                  p: 1.25,
                  mt: 1,
                }}
              >
                <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                  Campos Viatura
                </MDTypography>

                <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
                  <MDInput
                    label="Matrícula"
                    value={sel?.matricula || ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, matricula: (e.target.value ?? "").toUpperCase() }))
                    }
                    onBlur={(e) =>
                      setSel((p) => ({ ...p, matricula: formatMatriculaSe6(e.target.value) }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Data matrícula"
                    type="date"
                    size="medium"
                    fullWidth
                    value={sel?.matricula_data || ""}
                    onChange={(e) => setSel((p) => ({ ...p, matricula_data: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />

                  <MDInput
                    label="Peso bruto (Kg)"
                    type="number"
                    value={sel.peso_bruto_kg ?? ""}
                    onChange={(e) => setSel((p) => ({ ...p, peso_bruto_kg: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Tara (Kg)"
                    type="number"
                    value={sel.tara_kg ?? ""}
                    onChange={(e) => setSel((p) => ({ ...p, tara_kg: e.target.value }))}
                    fullWidth
                  />

                  <MDInput
                    label="Cilindrada (cm3)"
                    type="number"
                    value={sel.cilindrada_cm3 ?? ""}
                    onChange={(e) => setSel((p) => ({ ...p, cilindrada_cm3: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Potência máx (kW)"
                    type="number"
                    value={sel.potencia_max_kw ?? ""}
                    onChange={(e) => setSel((p) => ({ ...p, potencia_max_kw: e.target.value }))}
                    fullWidth
                  />

                  <MDInput
                    label="Lugares"
                    type="number"
                    value={sel.lugares ?? ""}
                    onChange={(e) => setSel((p) => ({ ...p, lugares: e.target.value }))}
                    fullWidth
                  />
                  <MDInput
                    label="Categoria"
                    value={sel.categoria ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, categoria: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Tipo veículo"
                    value={sel.tipo_veiculo ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({
                        ...p,
                        tipo_veiculo: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                  <MDInput
                    label="Tipo caixa"
                    value={sel.tipo_caixa ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, tipo_caixa: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Pneus frente"
                    value={sel.pneus_frente ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({
                        ...p,
                        pneus_frente: (e.target.value ?? "").toUpperCase(),
                      }))
                    }
                    fullWidth
                  />
                  <MDInput
                    label="Pneus trás"
                    value={sel.pneus_tras ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, pneus_tras: (e.target.value ?? "").toUpperCase() }))
                    }
                    fullWidth
                  />

                  <MDInput
                    label="Comprimento caixa carga (mm)"
                    type="number"
                    value={sel.comprimento_caixa_carga_mm ?? ""}
                    onChange={(e) =>
                      setSel((p) => ({ ...p, comprimento_caixa_carga_mm: e.target.value }))
                    }
                    fullWidth
                  />
                </MDBox>
              </MDBox>
            )}

            {/* ESTADO + OBS (em bloco) */}
            <MDBox sx={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: 2, p: 1.25 }}>
              <MDTypography variant="button" sx={{ display: "block", mb: 1 }}>
                Estado
              </MDTypography>

              <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
                <MDInput
                  label="Estado"
                  value={sel?.estado || ""}
                  onChange={(e) =>
                    setSel((p) => ({ ...p, estado: (e.target.value ?? "").toUpperCase() }))
                  }
                  fullWidth
                />

                <TextField
                  label="Data abate"
                  type="date"
                  size="medium"
                  fullWidth
                  value={sel?.data_abate || ""}
                  onChange={(e) => setSel((p) => ({ ...p, data_abate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>

              <MDInput
                label="Obs. estado"
                value={sel?.obs_estado || ""}
                onChange={(e) =>
                  setSel((p) => ({ ...p, obs_estado: (e.target.value ?? "").toUpperCase() }))
                }
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 1 }}
              />
            </MDBox>

            <FormControlLabel
              sx={{ ml: 0.5 }}
              control={
                <Checkbox
                  checked={Number(sel?.inativo || 0) === 1}
                  onChange={(e) =>
                    setSel((p) => ({
                      ...p,
                      inativo: e.target.checked ? 1 : 0,
                    }))
                  }
                  size="small"
                />
              }
              label="Inativo"
            />

            {/* RESPONSÁVEL (Autocomplete) */}
            <div>
              <MDTypography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Responsável
              </MDTypography>

              <Autocomplete
                options={utilizadores || []}
                getOptionLabel={getLabelResponsavel}
                isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                value={
                  (utilizadores || []).find((u) => Number(u.id) === Number(sel?.id_responsavel)) ||
                  null
                }
                onChange={(_, v) =>
                  setSel((p) => ({ ...p, id_responsavel: v ? Number(v.id) : null }))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="— selecionar —" size="small" fullWidth />
                )}
              />
            </div>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEdit(false);
              setDuaOcrSel(null);
              setSel(null);
              setImgMenu(null);
            }}
            color="secondary"
          >
            Cancelar
          </MDButton>
          <MDButton onClick={gravarEdicao} color="success">
            Guardar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* EVENTOS DO EQUIPAMENTO (DIALOG) */}
      <DialogEquipamentosEventos
        open={openEventos}
        onClose={() => {
          setOpenEventos(false);
          setEquipamentoEventos(null);
          setIdEventoAbrir(0);
        }}
        equipamento={equipamentoEventos}
        idEventoAbrir={idEventoAbrir}
      />

      {/* PASTAS E FICHEIROS DO EQUIPAMENTO (DIALOG) */}
      <DialogEquipamentoFicheiros
        open={openFicheiros}
        onClose={() => {
          setOpenFicheiros(false);
          setIdEquipFicheiros(0);
          setNomeEquipFicheiros("");
        }}
        idEquipamento={idEquipFicheiros}
        nomeEquipamento={nomeEquipFicheiros}
        acesso="Menu Equipamentos"
        subpastas={["Documentos", "Imagens", "Outros", "Eventos", "Confidencial"]}
      />
    </DashboardLayout>
  );
}
